import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader, Button } from "@/components/ui/PageHeader";
import { InputField, SelectField } from "@/components/ui/FormFields";
import { createInstitution } from "@/api/institutions";

const INSTITUTION_TYPES = [
  { value: "temple", label: "Temple" },
  { value: "gurudwara", label: "Gurudwara" },
  { value: "mosque", label: "Mosque" },
  { value: "church", label: "Church" },
  { value: "community_center", label: "Community center" },
  { value: "religious_school", label: "Religious school" },
  { value: "ashram", label: "Ashram" },
  { value: "mandir_society", label: "Mandir society" },
  { value: "other", label: "Other" },
];

const RELIGIONS = [
  { value: "hindu", label: "Hindu" },
  { value: "sikh", label: "Sikh" },
  { value: "muslim", label: "Muslim" },
  { value: "christian", label: "Christian" },
  { value: "jain", label: "Jain" },
  { value: "buddhist", label: "Buddhist" },
  { value: "zoroastrian", label: "Zoroastrian" },
  { value: "other_religion", label: "Other" },
];

export default function CreateInstitution() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    institutionType: "temple",
    religion: "hindu",
    tradition: "",
    city: "",
    state: "",
    country: "US",
    description: "",
    yearEstablished: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function setField(k: keyof typeof form, v: string) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const result = await createInstitution({
        name: form.name,
        institutionType: form.institutionType,
        religion: form.religion,
        tradition: form.tradition || undefined,
        city: form.city,
        state: form.state,
        country: form.country,
        description: form.description || undefined,
        yearEstablished: form.yearEstablished
          ? parseInt(form.yearEstablished, 10)
          : undefined,
      });
      navigate(`/lab/institutions/${result.data.slug}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Register an institution"
        subtitle="Creates the institution + you as its owner (OrgMember role=owner)."
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Institutions", href: "/lab/institutions" },
          { label: "Register" },
        ]}
      />

      <form
        onSubmit={handleSubmit}
        className="bg-white border border-gray-200 rounded-2xl p-5 max-w-2xl space-y-4"
      >
        <InputField
          label="Name"
          required
          value={form.name}
          onChange={(e) => setField("name", e.target.value)}
          maxLength={200}
        />
        <div className="grid grid-cols-2 gap-3">
          <SelectField
            label="Type"
            required
            value={form.institutionType}
            onChange={(e) => setField("institutionType", e.target.value)}
            options={INSTITUTION_TYPES}
          />
          <SelectField
            label="Religion"
            required
            value={form.religion}
            onChange={(e) => setField("religion", e.target.value)}
            options={RELIGIONS}
          />
        </div>
        <InputField
          label="Tradition (optional)"
          placeholder="e.g. ISKCON, Swaminarayan, Tamil"
          value={form.tradition}
          onChange={(e) => setField("tradition", e.target.value)}
        />
        <div className="grid grid-cols-3 gap-3">
          <InputField
            label="City"
            required
            value={form.city}
            onChange={(e) => setField("city", e.target.value)}
          />
          <InputField
            label="State"
            required
            value={form.state}
            onChange={(e) => setField("state", e.target.value)}
          />
          <InputField
            label="Country"
            value={form.country}
            onChange={(e) => setField("country", e.target.value)}
          />
        </div>
        <InputField
          label="Description (optional)"
          value={form.description}
          onChange={(e) => setField("description", e.target.value)}
          maxLength={5000}
        />
        <InputField
          label="Year established (optional)"
          type="number"
          value={form.yearEstablished}
          onChange={(e) => setField("yearEstablished", e.target.value)}
        />

        <Button type="submit" isLoading={submitting} disabled={!form.name.trim()}>
          Register
        </Button>
        {error && <p className="text-sm text-red-700">{error}</p>}
      </form>
    </div>
  );
}
